<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { dateHelpers, PrimaryButton } from '@hfc/ui-core';
import BaseSection from '@/application/shared/infrastructure/components/BaseSection.vue';
import QuestionVerificationCode from '@/shared/infrastructure/components/QuestionVerificationCode.vue';
import { APPLICATION_TYPES, UUID } from '@hfc/common';
import { useAppStore } from '@/shared/infrastructure/composition/useAppStore';
import { ChallengeType } from '@/application/shared/domain/ChallengeQuestionSettings';
import { useForm } from 'vee-validate';
import { useHandleApiError } from '@/shared/infrastructure/composition/useHandleApiError';
import { OtpChannelType } from '@/shared/domain/OtpChannelType';

interface EmitInterface {
  (event: 'resendVerificationCode'): void,
  (event: 'challengeSuccess'): void,
  (event: 'back'): void,
  (event: 'beforeSubmit'): void
}

const emits = defineEmits<EmitInterface>();

const { appStore, appState } = useAppStore();

const props = withDefaults(defineProps<{
  applicationType: APPLICATION_TYPES;
  challengeType: ChallengeType;
  loading: boolean;
  selectedChannel: OtpChannelType;
  uuid: UUID;
  buttonText?: string;
}>(), {
  buttonText: 'Validate'
});

const { fromUnixSecondsElapsed } = dateHelpers();
const { handleSfApiError } = useHandleApiError();
const { handleSubmit } = useForm();
const runValidations = handleSubmit(() => true);

const verificationCode = ref('');
const loadingLocal = ref(false);

const resendSeconds = ref(60);
let resendTimer: ReturnType<typeof setInterval> | null = null;

const onSubmit = async (): Promise<void> => {
  const valid = await runValidations();
  if (!valid) {
    return;
  }

  loadingLocal.value = true;
  const response = await appStore.sendVerificationCode(props.applicationType, props.challengeType, props.uuid, {
    code: verificationCode.value,
    channel: props.selectedChannel,
  });

  loadingLocal.value = false;
  if (response && response.error) {
    return handleSfApiError(response.error, true);
  }

  removeLocalStorageFlag();
  emits('challengeSuccess');
};

const beforeSubmit = async (): Promise<void> => {
  emits('beforeSubmit');
};

const clearResendTimer = (): void => {
  if (!resendTimer) {
    return;
  }

  clearInterval(resendTimer);
  resendTimer = null;
};

const startResendCountdown = (value?: number): void => {
  clearResendTimer();
  resendSeconds.value = value ?? 60;

  resendTimer = setInterval(() => {
    if (resendSeconds.value <= 1) {
      resendSeconds.value = 0;
      clearResendTimer();

      return;
    }

    resendSeconds.value -= 1;
  }, 1000);
};

const handleResend = (): void => {
  if (resendSeconds.value > 0) {
    return;
  }

  emits('resendVerificationCode');
  startResendCountdown();
};

const getChannelTypeLabel = (): string => {
  if (props.selectedChannel === OtpChannelType.SMS) {
    return 'SMS';
  }

  return 'Email';
};

const removeLocalStorageFlag = (): void => {
  localStorage.removeItem('challengeVerificationCodeSent');
};

onBeforeUnmount(() => {
  clearResendTimer();
});

onMounted(() => {
  const localStorageData = localStorage.getItem('challengeVerificationCodeSent');
  if (localStorageData) {
    const { timestamp } = JSON.parse(localStorageData);
    const elapsed = Math.floor(fromUnixSecondsElapsed(timestamp));
    if (elapsed < 60) {
      startResendCountdown(60 - elapsed);

      return;
    }
    removeLocalStorageFlag();
  }
  startResendCountdown();
});

defineExpose({ onSubmit });
</script>
<template>
  <BaseSection>
    <form @submit.prevent="beforeSubmit" @keydown.enter.prevent="beforeSubmit" class="flex w-full flex-col items-center min-h-[180px]">
      <div class="flex flex-col sm:flex-row justify-center sm:space-x-16">
        <QuestionVerificationCode v-model="verificationCode" :channel="props.selectedChannel" @complete="beforeSubmit" />
      </div>
      <div class="mt-4 text-sm text-gray-400">
        <hfc-icon icon="info-circle" class="inline-block mr-1" />
        Type the code we just sent you via {{ getChannelTypeLabel() }}.
      </div>
      <div class="flex w-full mt-12 text-sm text-gray-400 justify-center">
        Didn't receive the code?
        <button type="button" class="bg-white text-blue-400 underline disabled:no-underline disabled:text-gray-400 ml-2" :disabled="resendSeconds > 0" @click="handleResend">
          {{ resendSeconds > 0 ? `Resend (${resendSeconds}s)` : `Resend ${getChannelTypeLabel()}` }}
        </button>
      </div>
      <div class="flex w-full mt-4 text-sm text-gray-400 justify-center">
        Change verification method?<span class="ml-2 text-blue-400 underline cursor-pointer" @click="$emit('back')">Go back</span>
      </div>
    </form>
  </BaseSection>
  <div class="flex justify-center mt-10">
    <PrimaryButton @click="beforeSubmit" :square="false" size="xl" :is-loading="appState.loading || loadingLocal || loading">
      {{ buttonText }}
    </PrimaryButton>
  </div>
</template>
